import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Loader2, MapPin, PackageCheck, Truck, Printer, ClipboardList, FileDown, Building2 } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import ShippingLabel, { downloadLabelsPdf } from "@/components/ShippingLabel";

const SHIP_FROM_KEY = "shop.ship_from";
const DEFAULT_SHIP_FROM = "Pickle Bullpen Pro Shop\n(enter your return address)";

const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

function printSlip(o) {
  const rows = (o.line_items || []).map((l) => `<tr><td>${l.qty}</td><td>${esc(l.name.replace(/\s*\([^)]*\)\s*$/, ""))}</td><td class="v">${esc(l.variant?.size || "—")}</td><td class="v">${esc(l.variant?.color || "—")}</td><td class="chk">☐</td></tr>`).join("");
  const s = o.ship_to || {};
  const html = `<!doctype html><html><head><title>Packing Slip ${o.id.slice(-8)}</title><style>
    body{font-family:Arial,sans-serif;color:#111;padding:32px;max-width:720px;margin:0 auto}
    h1{font-size:22px;letter-spacing:2px;margin:0 0 4px}.k{font-size:11px;letter-spacing:3px;color:#666;font-weight:bold}
    .grid{display:flex;gap:40px;margin:20px 0}.grid div{flex:1}.lbl{font-size:10px;letter-spacing:2px;color:#666;font-weight:bold;margin-bottom:4px}
    table{width:100%;border-collapse:collapse;margin-top:12px}th{font-size:10px;letter-spacing:2px;text-align:left;border-bottom:2px solid #111;padding:6px 4px}
    td{padding:10px 4px;border-bottom:1px solid #ddd;font-size:14px}.v{font-weight:900;font-size:16px;text-transform:uppercase}.chk{font-size:20px;text-align:center}
    .foot{margin-top:28px;font-size:11px;color:#666}@media print{body{padding:0}}</style></head><body>
    <div class="k">PICKLE BULLPEN PRO SHOP</div><h1>PACKING SLIP / PICK LIST</h1>
    <div class="grid"><div><div class="lbl">ORDER</div>#${o.id.slice(-8).toUpperCase()}<br/>${esc(String(o.created_at || "").slice(0, 10))} · ${esc(o.paid_via)} · $${Number(o.amount).toFixed(2)}</div>
    <div><div class="lbl">SHIP TO</div><b>${esc(s.name)}</b><br/>${esc(s.street)}<br/>${esc(s.city)}, ${esc(s.state)} ${esc(s.zip)}<br/>${esc(o.email)}</div></div>
    <table><thead><tr><th>QTY</th><th>ITEM</th><th>SIZE</th><th>COLOR</th><th>PICKED</th></tr></thead><tbody>${rows}</tbody></table>
    <div class="foot">Check each line as you pack. Size and color must match exactly — when in doubt, re-check before sealing.</div>
    <script>window.onload=function(){window.print();}</script></body></html>`;
  const w = window.open("", "_blank", "width=800,height=900");
  if (!w) return toast.error("Pop-up blocked — allow pop-ups to print the packing slip");
  w.document.write(html); w.document.close();
}

function PickList({ o }) {
  return (
    <div className="mt-2 border border-pb-border/60 bg-black/30" data-testid={`order-picklist-${o.id}`}>
      <div className="flex items-center gap-1.5 px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.25em] text-pb-cyan border-b border-pb-border/60"><ClipboardList className="w-3 h-3" /> Pick List</div>
      {(o.line_items || []).map((l, i) => (
        <div key={i} className="flex flex-wrap items-center gap-2 px-2.5 py-1.5 border-b border-pb-border/30 last:border-b-0 text-xs" data-testid={`order-pick-${o.id}-${i}`}>
          <span className="font-mono font-black text-white w-7">{l.qty}×</span>
          <span className="flex-1 min-w-[120px] text-zinc-200">{l.name.replace(/\s*\([^)]*\)\s*$/, "")}</span>
          {l.variant?.size && <span className="border border-pb-red text-pb-red font-black uppercase px-2 py-0.5 text-[11px]" data-testid={`order-pick-size-${o.id}-${i}`}>Size {l.variant.size}</span>}
          {l.variant?.color && <span className="border border-pb-cyan text-pb-cyan font-black uppercase px-2 py-0.5 text-[11px]" data-testid={`order-pick-color-${o.id}-${i}`}>{l.variant.color}</span>}
          {!l.variant?.size && !l.variant?.color && <span className="text-[10px] uppercase tracking-widest text-zinc-600">no options</span>}
        </div>
      ))}
    </div>
  );
}

export default function OrdersPanel() {
  const [orders, setOrders] = useState([]);
  const [tracking, setTracking] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [batchBusy, setBatchBusy] = useState(false);
  const { content, save } = useContent();
  const shipFrom = content?.[SHIP_FROM_KEY] || "";
  const [fromDraft, setFromDraft] = useState(null);

  const saveFrom = async () => { if (await save(SHIP_FROM_KEY, fromDraft.trim(), { quiet: true })) { toast.success("Ship-from address saved for every label"); setFromDraft(null); } };
  const batchPdf = async (list) => {
    setBatchBusy(true);
    try { await downloadLabelsPdf(list, shipFrom, `labels_pending_${new Date().toISOString().slice(0, 10)}.pdf`); toast.success(`${list.length} label${list.length === 1 ? "" : "s"} in one PDF`); }
    catch { toast.error("Could not build the PDF"); }
    finally { setBatchBusy(false); }
  };

  const load = () => api.get("/admin/orders").then(({ data }) => setOrders(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const ship = async (o) => {
    setBusyId(o.id);
    try {
      const t = tracking[o.id] || {};
      await api.post(`/admin/orders/${o.id}/ship`, { tracking: t.tracking || "", carrier: t.carrier || "" });
      toast.success(`Marked shipped — ${o.ship_to?.name} has been emailed`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  const pending = orders.filter((o) => o.status !== "shipped");
  const shipped = orders.filter((o) => o.status === "shipped");

  return (
    <div className="border border-pb-border bg-pb-surface p-5" data-testid="orders-panel">
      <div className="flex items-center gap-2 mb-1"><Truck className="w-4 h-4 text-pb-cyan" /><span className="font-display font-bold uppercase">Order Fulfillment</span></div>
      <p className="text-xs text-zinc-500 mb-4">
        Every paid Pro Shop order with physical items lands here with the member's ship-to address. Add tracking (optional)
        and hit Mark Shipped — the member gets a branded shipping email plus an in-app notification automatically.
      </p>
      {orders.length === 0 && <div className="text-xs text-zinc-600">No physical orders yet — they'll appear here the moment someone checks out with shippable gear.</div>}

      <div className="border border-pb-border bg-black/30 p-3 mb-4" data-testid="ship-from-box">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
          <span className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-[0.25em] text-zinc-400"><Building2 className="w-3 h-3" /> Ship-From / Return Address (printed on every label)</span>
          {fromDraft === null ? (
            <button data-testid="ship-from-edit" onClick={() => setFromDraft(shipFrom || DEFAULT_SHIP_FROM)} className="text-[9px] uppercase tracking-widest text-pb-cyan hover:text-white">Edit</button>
          ) : (
            <div className="flex gap-2">
              <button data-testid="ship-from-save" onClick={saveFrom} className="bg-pb-cyan text-black px-3 py-1 text-[9px] font-black uppercase tracking-widest">Save</button>
              <button data-testid="ship-from-cancel" onClick={() => setFromDraft(null)} className="text-[9px] uppercase tracking-widest text-zinc-500 hover:text-white">Cancel</button>
            </div>
          )}
        </div>
        {fromDraft === null ? (
          <pre className={`font-mono text-[11px] whitespace-pre-wrap ${shipFrom ? "text-zinc-200" : "text-amber-400"}`} data-testid="ship-from-text">{shipFrom || "Not set — labels will show only “PICKLE BULLPEN PRO SHOP”. Click Edit to add your street address."}</pre>
        ) : (
          <textarea data-testid="ship-from-input" value={fromDraft} rows={4} onChange={(e) => setFromDraft(e.target.value)}
            className="w-full bg-pb-elevated border border-pb-border px-2.5 py-1.5 font-mono text-[11px] text-white focus:outline-none focus:border-pb-cyan resize-y" />
        )}
      </div>

      {pending.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <div className="text-[9px] font-black uppercase tracking-[0.25em] text-amber-400">Awaiting shipment ({pending.length})</div>
          <button data-testid="labels-batch-pdf" onClick={() => batchPdf(pending)} disabled={batchBusy}
            className="flex items-center gap-1.5 bg-white text-black hover:bg-[#BEF264] px-3 py-1.5 text-[9px] font-black uppercase tracking-widest transition-colors disabled:opacity-50">
            {batchBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileDown className="w-3 h-3" />} All Pending Labels → PDF ({pending.length})
          </button>
        </div>
      )}
      <div className="space-y-3">
        {pending.map((o) => (
          <div key={o.id} className="border border-amber-500/30 bg-pb-elevated/40 p-3" data-testid={`order-${o.id}`}>
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[180px]">
                <div className="text-sm font-bold">{(o.line_items || []).map((l) => `${l.qty}× ${l.name}`).join(", ")}</div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                  ${Number(o.amount).toFixed(2)} · {o.paid_via} · {String(o.created_at || "").slice(0, 10)} · {o.email}
                </div>
              </div>
              {o.ship_to && (
                <div className="text-xs text-zinc-300 min-w-[160px]" data-testid={`order-shipto-${o.id}`}>
                  <div className="flex items-center gap-1 text-[9px] uppercase tracking-widest text-zinc-500 mb-0.5"><MapPin className="w-2.5 h-2.5" /> Ship to</div>
                  <div className="font-semibold">{o.ship_to.name}</div>
                  <div>{o.ship_to.street}</div>
                  <div>{o.ship_to.city}, {o.ship_to.state} {o.ship_to.zip}</div>
                </div>
              )}
            </div>
            <PickList o={o} />
            <ShippingLabel o={o} shipFrom={shipFrom} />
            <div className="flex flex-wrap items-center gap-2 mt-2.5">
              <button data-testid={`order-print-${o.id}`} onClick={() => printSlip(o)} title="Print packing slip / pick list"
                className="flex items-center gap-1.5 border border-pb-border hover:border-white text-zinc-300 hover:text-white px-3 py-2 text-[10px] font-black uppercase tracking-widest transition-colors">
                <Printer className="w-3.5 h-3.5" /> Packing Slip
              </button>
              <input data-testid={`order-carrier-${o.id}`} value={tracking[o.id]?.carrier || ""} placeholder="Carrier (USPS…)"
                onChange={(e) => setTracking((t) => ({ ...t, [o.id]: { ...t[o.id], carrier: e.target.value } }))}
                className="w-28 bg-pb-elevated border border-pb-border px-2 py-1.5 text-xs text-white placeholder-zinc-600 focus:outline-none focus:border-pb-cyan" />
              <input data-testid={`order-tracking-${o.id}`} value={tracking[o.id]?.tracking || ""} placeholder="Tracking # (optional)"
                onChange={(e) => setTracking((t) => ({ ...t, [o.id]: { ...t[o.id], tracking: e.target.value } }))}
                className="flex-1 min-w-[140px] bg-pb-elevated border border-pb-border px-2 py-1.5 text-xs text-white placeholder-zinc-600 focus:outline-none focus:border-pb-cyan" />
              <button data-testid={`order-ship-${o.id}`} onClick={() => ship(o)} disabled={busyId === o.id}
                className="flex items-center gap-1.5 bg-pb-cyan text-black font-black uppercase tracking-widest px-4 py-2 text-[10px] hover:opacity-90 disabled:opacity-40">
                {busyId === o.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PackageCheck className="w-3.5 h-3.5" />} Mark Shipped
              </button>
            </div>
          </div>
        ))}
      </div>

      {shipped.length > 0 && (
        <>
          <div className="text-[9px] font-black uppercase tracking-[0.25em] text-emerald-400 mt-5 mb-2">Shipped ({shipped.length})</div>
          <div className="divide-y divide-pb-border/40">
            {shipped.map((o) => (
              <div key={o.id} className="py-2 flex flex-wrap items-center gap-3 text-xs" data-testid={`order-shipped-${o.id}`}>
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                <span className="flex-1 text-zinc-300">{(o.line_items || []).map((l) => `${l.qty}× ${l.name}`).join(", ")} → {o.ship_to?.name}</span>
                {o.tracking && <span className="font-mono text-zinc-500">{o.carrier} {o.tracking}</span>}
                <button data-testid={`order-print-shipped-${o.id}`} onClick={() => printSlip(o)} title="Reprint packing slip" className="text-zinc-600 hover:text-white"><Printer className="w-3.5 h-3.5" /></button>
                <span className="text-zinc-600">{String(o.shipped_at || "").slice(0, 10)}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
